import React, { useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import Swal from 'sweetalert2';
import { obtenerReembolsosGira, aprobarReembolso, rechazarReembolso } from "../../api/reembolsos.ts";

export default function AdminReembolsosPage() {
    const { t } = useContext(TranslatorContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [giraId, setGiraId] = useState(null);
    const [reembolsos, setReembolsos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedReembolso, setSelectedReembolso] = useState(null);
    const [accion, setAccion] = useState(null); // 'aprobar' o 'rechazar'
    const [montoAprobado, setMontoAprobado] = useState('');
    const [observaciones, setObservaciones] = useState('');

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get('gira');
        if (id) {
            setGiraId(id);
        } else {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'ID de gira no especificado'
            }).then(() => navigate('/giras'));
        }
    }, [location, navigate]);

    useEffect(() => {
        if (giraId) {
            cargarReembolsos();
        }
    }, [giraId]);

    const cargarReembolsos = async () => {
        try {
            setLoading(true);
            const data = await obtenerReembolsosGira(giraId);
            if (data.error) {
                throw new Error(data.msg || 'Error al cargar reembolsos');
            }
            setReembolsos(data.reembolsos || []);
        } catch (error) {
            console.error('Error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al cargar los reembolsos'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAprobar = (reembolso) => {
        setSelectedReembolso(reembolso);
        setAccion('aprobar');
        setMontoAprobado(reembolso.reem_monto_solicitado);
        setObservaciones('');
        setShowModal(true);
    };

    const handleRechazar = (reembolso) => {
        setSelectedReembolso(reembolso);
        setAccion('rechazar');
        setMontoAprobado('');
        setObservaciones('');
        setShowModal(true);
    };

    const handleConfirmar = async () => {
        if (!selectedReembolso) return;

        if (accion === 'aprobar' && !montoAprobado) {
            Swal.fire({
                icon: 'warning',
                text: 'Debe especificar el monto aprobado'
            });
            return;
        }

        Swal.fire({
            title: 'Procesando...',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        try {
            let result;
            if (accion === 'aprobar') {
                result = await aprobarReembolso(selectedReembolso.reem_id, {
                    montoAprobado: parseFloat(montoAprobado),
                    observaciones
                });
            } else {
                result = await rechazarReembolso(selectedReembolso.reem_id, {
                    motivo: observaciones
                });
            }

            if (result.error) {
                throw new Error(result.msg || 'Error al procesar');
            }

            Swal.close();
            Swal.fire({
                icon: 'success',
                title: accion === 'aprobar' ? 'Reembolso Aprobado' : 'Reembolso Rechazado',
                text: `El reembolso ha sido ${accion === 'aprobar' ? 'aprobado' : 'rechazado'} correctamente`
            }).then(() => {
                setShowModal(false);
                cargarReembolsos();
            });
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al procesar reembolso'
            });
        }
    };

    const getEstadoColor = (estado) => {
        const colores = {
            'Solicitado': '#ff9800',
            'En Revisión': '#2196f3',
            'Aprobado': '#4caf50',
            'Rechazado': '#f44336',
            'Reembolsado': '#9c27b0'
        };
        return colores[estado] || '#999';
    };

    const getEstadoBadge = (estado) => {
        return (
            <span style={{
                backgroundColor: getEstadoColor(estado),
                color: 'white',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: 'bold'
            }}>
                {estado}
            </span>
        );
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Administración de Reembolsos</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">Administración</Link></li>
                                <li className="mc-breadcrumb-item">Reembolsos</li>
                            </ul>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header">
                            <h4 className="mc-card-title">Solicitudes de Reembolso</h4>
                            <button 
                                type='button' 
                                className='btn btn-sm btn-primary'
                                onClick={() => navigate(-1)}
                            >
                                <i className='material-icons' style={{ marginRight: '5px' }}>arrow_back</i>
                                Volver
                            </button>
                        </div>

                        {loading ? (
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                <p>Cargando reembolsos...</p>
                            </div>
                        ) : reembolsos.length === 0 ? (
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                <p>No hay solicitudes de reembolso para esta gira</p>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table table-hover" style={{ marginBottom: 0 }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Alumno</th>
                                            <th>Tipo</th>
                                            <th>Motivo</th>
                                            <th>Monto Solicitado</th>
                                            <th>Estado</th>
                                            <th>Fecha Solicitud</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reembolsos.map((reembolso, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <small>{reembolso.alu_nombres} {reembolso.alu_apellidos}</small>
                                                </td>
                                                <td>{reembolso.reem_tipo}</td>
                                                <td>{reembolso.reem_motivo}</td>
                                                <td>
                                                    ${parseFloat(reembolso.reem_monto_solicitado).toLocaleString('es-CL', { 
                                                        minimumFractionDigits: 2, 
                                                        maximumFractionDigits: 2 
                                                    })}
                                                </td>
                                                <td>{getEstadoBadge(reembolso.reem_estado)}</td>
                                                <td>
                                                    <small>{reembolso.reem_fecha_solicitud}</small>
                                                </td>
                                                <td>
                                                    {reembolso.reem_estado === 'Solicitado' && (
                                                        <>
                                                            <button
                                                                type='button'
                                                                className='btn btn-sm btn-success me-2'
                                                                onClick={() => handleAprobar(reembolso)}
                                                                title='Aprobar'
                                                            >
                                                                <i className='material-icons'>check_circle</i>
                                                            </button>
                                                            <button
                                                                type='button'
                                                                className='btn btn-sm btn-danger'
                                                                onClick={() => handleRechazar(reembolso)}
                                                                title='Rechazar'
                                                            >
                                                                <i className='material-icons'>cancel</i>
                                                            </button>
                                                        </>
                                                    )}
                                                    {reembolso.reem_estado !== 'Solicitado' && (
                                                        <span style={{ color: '#999' }}>
                                                            {reembolso.reem_estado === 'Aprobado' ? 'Aprobado' : 'Procesado'}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Modal para aprobar/rechazar */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {accion === 'aprobar' ? 'Aprobar Reembolso' : 'Rechazar Reembolso'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedReembolso && (
                        <>
                            <div className="mb-3">
                                <label><strong>Alumno:</strong></label>
                                <p>{selectedReembolso.alu_nombres} {selectedReembolso.alu_apellidos}</p>
                            </div>
                            <div className="mb-3">
                                <label><strong>Tipo de Reembolso:</strong></label>
                                <p>{selectedReembolso.reem_tipo}</p>
                            </div>
                            <div className="mb-3">
                                <label><strong>Motivo:</strong></label>
                                <p>{selectedReembolso.reem_motivo}</p>
                            </div>
                            <div className="mb-3">
                                <label><strong>Monto Solicitado:</strong></label>
                                <p>${parseFloat(selectedReembolso.reem_monto_solicitado).toLocaleString('es-CL', { 
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 2 
                                })}</p>
                            </div>

                            {accion === 'aprobar' && (
                                <Form.Group className="mb-3">
                                    <Form.Label><strong>Monto a Aprobar *</strong></Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={montoAprobado}
                                        onChange={(e) => setMontoAprobado(e.target.value)}
                                        placeholder="Monto"
                                        min="0"
                                        step="0.01"
                                    />
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Observaciones</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={observaciones}
                                    onChange={(e) => setObservaciones(e.target.value)}
                                    placeholder={accion === 'aprobar' ? 'Observaciones (opcional)' : 'Motivo del rechazo'}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancelar
                    </Button>
                    <Button 
                        variant={accion === 'aprobar' ? 'success' : 'danger'}
                        onClick={handleConfirmar}
                    >
                        {accion === 'aprobar' ? 'Aprobar' : 'Rechazar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}
